/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from "react"
import {
  Box,
  Container,
  makeStyles,
  Paper,
  TableCell,
  TableRow
} from "@material-ui/core"
import PropTypes from "prop-types"
import ThemeProvider from "@material-ui/styles/ThemeProvider"
import { useSelector } from "react-redux"
import WidgetSkeleton from "../admin/widgets/WidgetSkeleton"
import CircularProgressBox from "../../component/CircularProgressBox"
import useWidgetData from "../../hooks/useWidgetData"
import WidgetErrorFallback from "../../component/widgets/WidgetErrorFallback"
import PageSubTitle from "../../component/header/PageSubTitle"
import MuiDatatablesAdapter from "../../component/widgets/table/MuiDatatablesAdapter"
import ErrorBoundary from "../error/ErrorBoundary"
import { tableTheme } from "../../styles"
import DataTable from "../../component/tables/DataTable"
import Widget from "./Widget"
import { pages } from "../../widgets/pages"
import WidgetElement from "../../component/widgets/WidgetElement"
import highchartsPluginRegistry from "../../component/widgets/chart/HighchartsPluginRegistry"
import { PAGE_IDENTIFIER } from "./pages"

const useStyles = makeStyles((theme) => ({
  rowWithDivider: {
    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    borderBottomColor: theme.palette.grey[800]
  },
  boldCell: {
    "& > span": {
      fontWeight: 600
    }
  },
  italicizedCell: {
    "& > span": {
      fontStyle: "italic"
    }
  },
  tableBackgroundColorTextCell: {
    "& > span": {
      color: theme.palette.tableHead.backgroundColor
    }
  },
  headingCell: {
    fontSize: "inherit",
    color: "inherit",
    padding: "0 !important"
  },
  firstHeadingCell: {
    backgroundColor: "white !important",
    paddingRight: "16px !important",
    fontStyle: "italic"
  }
}))

/**
 * The ID of the table that requires custom formatting.
 * @type {string}
 */
const CUSTOM_FORMAT_TABLE_ID = "13d86e51-77f1-4dd6-bd49-581a17f24fd4"

/**
 * A performance table with custom styles
 * @param data
 * @returns {JSX.Element}
 * @constructor
 */
function CustomPerformanceTable({ data }) {
  const classes = useStyles()
  const adapter = new MuiDatatablesAdapter(data.data)
  const rows = adapter.mapRows()
  const columns = adapter.mapColumns().map((column, index) => {
    return {
      ...column,
      options: {
        ...column.options,
        customHeadRender(columnMeta) {
          const { label } = columnMeta
          const className =
            index === 1 ? classes.firstHeadingCell : classes.headingCell

          return (
            <TableCell
              className={className}
              variant="head"
              padding="normal"
              align={index === 1 ? "right" : "center"}
            >
              {label}
            </TableCell>
          )
        }
      }
    }
  })

  function customRowRender(cells, dataIndex, rowIndex) {
    cells.shift() // The first element is the row index, remove it
    const withDivider = rowIndex === 2 || rowIndex === 5

    function getCellClassName(columnIndex) {
      const isFirstColumn = columnIndex === 0
      const isBolded =
        (rowIndex === 3 && isFirstColumn) || // Plus/Moins values sur le mois (row header)
        (rowIndex === 5 && isFirstColumn) || // Performances des avoirs (row header)
        rowIndex === 7 // Performances sur l'année (entire row)

      const isItalicized = rowIndex === 5 || rowIndex === 6
      const isBlue = rowIndex === 5

      const cellClasses = []

      if (isBolded) {
        cellClasses.push(classes.boldCell)
      }

      if (isItalicized) {
        cellClasses.push(classes.italicizedCell)
      }

      if (isBlue) {
        cellClasses.push(classes.tableBackgroundColorTextCell)
      }

      return cellClasses.join(" ")
    }

    // Empty row
    if (rowIndex === 4) {
      return <Box py={2} />
    }

    return (
      <TableRow className={withDivider ? classes.rowWithDivider : null}>
        {cells.map((cell, index) => (
          <TableCell align={index === 0 ? "left" : "center"} key={cell}>
            <div className={getCellClassName(index)}>{cell}</div>
          </TableCell>
        ))}
      </TableRow>
    )
  }

  const options = {
    ...adapter.options,
    customRowRender
  }

  return (
    <Box component={Paper} elevation={0}>
      <ThemeProvider theme={tableTheme}>
        <DataTable options={options} columns={columns} data={rows} />
      </ThemeProvider>
    </Box>
  )
}

CustomPerformanceTable.propTypes = {
  data: PropTypes.object.isRequired
}

function PortfolioPerformanceWidget({ widgetId, subscriptions, page }) {
  const { widgetPreview, data, loading, error } = useWidgetData({
    widgetId,
    subscriptions
  })

  let customTable
  let otherWidgets = []

  if (data?.id === CUSTOM_FORMAT_TABLE_ID) {
    customTable = data
  } else if (Array.isArray(data?.items)) {
    customTable = data.items.find((item) => item.id === CUSTOM_FORMAT_TABLE_ID)
    otherWidgets = data.items.filter((item) => item !== customTable)
  } else if (data) {
    otherWidgets = Array.isArray(data) ? data : [data]
  }

  if (widgetPreview) {
    return <WidgetSkeleton widgetId={widgetId} page={page} />
  }

  if (loading) {
    return <CircularProgressBox height="100vh" />
  }

  if (error || (customTable == null && otherWidgets.length === 0)) {
    return <WidgetErrorFallback type={widgetId} />
  }

  return (
    <ErrorBoundary type="Table" FallbackComponent={WidgetErrorFallback}>
      <Box display="flex" flexDirection="column" gridGap={16}>
        {customTable && <CustomPerformanceTable data={customTable} />}
        {otherWidgets.map((it) => (
          <WidgetElement key={it.id} widgetId={it.id} data={it} />
        ))}
      </Box>
    </ErrorBoundary>
  )
}

PortfolioPerformanceWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  subscriptions: PropTypes.array.isRequired,
  page: PropTypes.oneOf(pages).isRequired
}

export default function PerformanceCustomTab({
  children,
  subtitle,
  renderSelector,
  TabContentDSL
}) {
  const [portfolioPerformanceWidget, ...otherWidgets] = TabContentDSL
  const { alias: widgetId, subscriptions, page } = portfolioPerformanceWidget
  const wealthPages = useSelector((state) => state.wealth.pages)
  const [hasMax, setHasMax] = useState(false)
  useEffect(() => {
    const performancePage = wealthPages.find(
      ({ pageIdentifier }) => pageIdentifier === PAGE_IDENTIFIER.performance
    )
    setHasMax(performancePage.dateIsLimited)

    // Register the default plotOptionsPlugin if it doesn't exist
    if (
      highchartsPluginRegistry.getPlugins("plotOptionsPlugin") === undefined
    ) {
      highchartsPluginRegistry.register("plotOptionsPlugin", (chartOpts) => {
        const newChartOpts = JSON.parse(JSON.stringify(chartOpts))
        newChartOpts.plotOptions.series.compare = "percent"
        newChartOpts.plotOptions.series.compareBase = 100
        newChartOpts.plotOptions.series.compareStart = true
        return newChartOpts
      })
    }
    return () => highchartsPluginRegistry.remove("plotOptionsPlugin")
  }, [])

  return (
    <Box py={3}>
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center">
          {renderSelector?.({ hasMax })}
        </Box>
        <PageSubTitle subtitle={subtitle} />
        {children}
      </Container>
      <Container maxWidth="xl">
        <PortfolioPerformanceWidget
          widgetId={widgetId}
          subscriptions={subscriptions}
          page={page}
        />
        {otherWidgets.map((it) => (
          <Widget key={it.alias} {...it} />
        ))}
      </Container>
    </Box>
  )
}

PerformanceCustomTab.propTypes = {
  subtitle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    defaultMessage: PropTypes.string.isRequired
  }).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  renderSelector: PropTypes.func,
  TabContentDSL: PropTypes.any.isRequired
}

PerformanceCustomTab.defaultProps = {
  children: null,
  renderSelector: undefined
}
